import CommonLookupDisplay from "components/CommonLookupDisplay";
import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IFireStation, { IFireStationCreateOrUpdate, IFireStationFilters, IFireStationInitResult } from "models/sql/IFireStation";
import { useContext, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import FireStationService from "services/FireStationService";
import { applyGlobalFilterData } from "utilities/MiscUtilities";

interface IFireStationsLookupProps {
    baseData?: IFireStationInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const FireStationsLookup = (props: IFireStationsLookupProps) => {
    const [filter, setFilter] = useState<IFireStationFilters>({});
    const navigate = useNavigate();
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        FireStationService.DownloadExcelFileAsync(
            accessToken,
            "FireStationExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const getColumns = (): IColumnDefinition<IFireStation, IFireStationFilters>[] => {
        return [
            createFilterableArrayColumnDefinition<IFireStation, IFireStationFilters>(
                globalContext,
                "number",
                "numbers",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`number${row.fireStationId}`}>
                    {
                        (row.number || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            createFilterableArrayColumnDefinition<IFireStation, IFireStationFilters>(
                globalContext,
                "name",
                "names",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`name${row.fireStationId}`}>
                    {
                        (row.name || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            {
                propName: "city",
                title: "City",
                sortable: true
            },
            createFilterableArrayColumnDefinition<IFireStation, IFireStationFilters>(
                globalContext,
                "state",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`state${row.fireStationId}`}>
                    {
                        (row.state || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            createFilterableArrayColumnDefinition<IFireStation, IFireStationFilters>(
                globalContext,
                "country",
                "countries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`countries${row.fireStationId}`}>
                    {
                        (row.country || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            createFilterableArrayColumnDefinition<IFireStation, IFireStationFilters>(
                globalContext,
                "postalCode",
                "postalCodes",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`postalCode${row.fireStationId}`}>
                    {
                        (row.postalCode || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>)
        ];
    };

    const getTools = () => [
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Station/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IFireStationFilters, IFireStation, IFireStationCreateOrUpdate>
            baseFilters={{
                sortBys: ["fireStationId"],
                sortDescending: true
            }}
            filterHints={{
                stationIds: "Array",
                postalCodes: "Array",
                hasCancerCommittee: "Boolean",
                hasSafetyCommittee: "Boolean",
                hasSleepingQuarters: "Boolean",
                hasLargeCeilingFansInBay: "Boolean",
                hasLargeHeatingInBay: "Boolean",
                numbers: "Array",
                countries: "Array",
                ppestorages: "Array",
                airDecons: "Array",
                vehicleTypes: "Array",
                names: "Array",
                sortBys: "Array",
                sortDescending: "Boolean"
            }}
            dataTypeName="Station"
            dataTypeNamePlural="Stations"
            pathName="Station"
            primaryIdKey="fireStationId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={FireStationService}
            tools={getTools()}
        />
    );
}

export default FireStationsLookup;
